import {useMemo} from 'react'
import {useSubscription} from 'react-relay'
import type {GraphQLSubscriptionConfig} from 'relay-runtime'
import {graphql} from 'relay-runtime'

import type {ParentIssueSubscription} from './__generated__/ParentIssueSubscription.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const subscription = graphql`
  subscription ParentIssueSubscription($issueId: ID!) {
    issueUpdated(id: $issueId) {
      # This could be made more efficient by splitting the fragment into subparts, but it's already pretty small
      issueStateUpdated {
        id
        title
        titleHTML
        url
        number
        repository {
          nameWithOwner
        }
        state
        stateReason
        subIssuesSummary {
          total
          completed
        }
        parent {
          id
          number
          repository {
            name
            owner {
              login
            }
          }
          state
          stateReason
          title
          titleHTML
          url
        }
      }
      issueTitleUpdated {
        id
        title
        titleHTML
        url
        number
        repository {
          nameWithOwner
        }
        state
        stateReason
        subIssuesSummary {
          total
          completed
        }
        parent {
          id
          number
          repository {
            name
            owner {
              login
            }
          }
          state
          stateReason
          title
          titleHTML
          url
        }
      }
      subIssuesSummaryUpdated {
        subIssuesSummary {
          total
          completed
        }
      }
    }
  }
`

export const useParentIssueSubscription = (issueId: string) => {
  const config = useMemo<GraphQLSubscriptionConfig<ParentIssueSubscription>>(
    () => ({
      variables: {issueId},
      subscription,
    }),
    [issueId],
  )

  useSubscription(config)
}

/**
 * Render-null component that opens the `useParentIssueSubscription`.
 *
 * Mount this conditionally (e.g. behind `isLoggedIn()`) so that the underlying
 * subscription only runs when the viewer is authenticated. The `issueUpdated`
 * subscription requires an authenticated viewer; opening it as an anonymous
 * user causes the server to respond with FORBIDDEN and the fetch-graphql layer
 * to throw "Expected data property in response". See github/issues#22981.
 */
export function ParentIssueSubscriber({issueId}: {issueId: string}) {
  useParentIssueSubscription(issueId)
  return null
}
